import React from 'react'
import { Badge, Box, CircularProgress, Fab } from '@mui/material'
import TranslateIcon from '@mui/icons-material/Translate'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { useTranslatorWidget } from './TranslatorProvider'
import usePointerDrag, { placementStyle } from './usePointerDrag'

const BUBBLE_SIZE_PX = 56
const BUBBLE_MARGIN_PX = 24
// Same layer as the panel: above modal dialogs (1300), below snackbars (1400).
const BUBBLE_Z_INDEX = 1350

/**
 * The minimized translator (#2892, spec §7): a draggable MIC-green bubble. While a translation
 * runs, an indeterminate ring surrounds it; once it settles (success or failure) a badge shows
 * until the user opens the panel again.
 */
const TranslatorBubble = () => {
  const { t_ } = useSmartTranslation()
  const { ui, open, setBubblePosition, translator } = useTranslatorWidget()
  // The bubble has its own position: it parks at the bottom-left corner when minimized and only
  // moves when the user drags it; the panel keeps its place for when it is reopened.
  const { handleProps } = usePointerDrag({
    position: ui.bubblePosition,
    onMove: setBubblePosition,
    bounds: { width: BUBBLE_SIZE_PX, height: BUBBLE_SIZE_PX }
  })

  return (
    <Box
      sx={{
        position: 'fixed',
        zIndex: BUBBLE_Z_INDEX,
        width: BUBBLE_SIZE_PX,
        height: BUBBLE_SIZE_PX
      }}
      style={placementStyle(ui.bubblePosition, BUBBLE_MARGIN_PX, 'bottom-left')}
    >
      {translator.translating
        ? (
          <CircularProgress
            size={BUBBLE_SIZE_PX}
            thickness={2}
            color='primary'
            sx={{ position: 'absolute', top: 0, left: 0 }}
          />
          )
        : null}
      <Fab
        color='primary'
        size='medium'
        aria-label={t_(ui.badge ? 'Translation finished' : 'Translator')}
        title={t_(ui.badge ? 'Translation finished' : 'Translator')}
        onClick={open}
        {...handleProps}
        style={{ ...handleProps.style, position: 'absolute', top: 4, left: 4 }}
      >
        <Badge variant='dot' color='error' invisible={!ui.badge} overlap='circular'>
          <TranslateIcon />
        </Badge>
      </Fab>
    </Box>
  )
}

export default TranslatorBubble
